import type { SEOHeadProps } from '../components/SEOHead';

// Default SEO configuration
export const defaultSEO: SEOHeadProps = {
  title: "Rulevia - AI Ad Creative Generator & Campaign Optimizer | Multi-Platform",
  description: "Create high-performing ad creatives with AI in minutes. Rulevia generates, optimizes, and validates ads for Meta, Google, TikTok & more. Get compliance checks, performance predictions, and first-time approval. Start free trial.",
  keywords: "AI ad generator, ad creative generator, campaign optimizer, multi-platform ads, Meta ads, Google ads, TikTok ads, ad compliance, performance prediction, ad validation, first-time approval, AI advertising",
  image: "/page_thumbnail.jpg",
  type: "website",
  twitterHandle: "@rulevia"
};

// Organization structured data
export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Rulevia",
  "url": "https://rulevia.com",
  "logo": "https://rulevia.com/rulevia_logo_square.png",
  "description": "AI-powered creative compliance platform for advertising",
  "foundingDate": "2024",
  "industry": "Marketing Technology",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@rulevia.com"
  },
  "sameAs": [
    "https://twitter.com/rulevia",
    "https://linkedin.com/company/rulevia"
  ]
};

// Page-specific SEO configurations
export const pageSEOConfig: Record<string, Partial<SEOHeadProps>> = {
  // Public pages
  "/": {
    title: "Rulevia - AI Ad Creative Generator & Campaign Optimizer | Multi-Platform",
    description: "Create high-performing ad creatives with AI in minutes. Rulevia generates, optimizes, and validates ads for Meta, Google, TikTok & more. Get compliance checks, performance predictions, and first-time approval. Start free trial.",
    url: "/",
    structuredData: organizationStructuredData
  },

  "/blog": {
    title: "Blog - Ad Compliance & Creative Optimization Tips | Rulevia",
    description: "Discover expert insights on ad compliance, creative optimization, and AI-powered advertising strategies. Stay updated with Rulevia's latest tips and industry trends.",
    url: "/blog",
    type: "website"
  },

  "/contact": {
    title: "Contact Us - Get in Touch with Rulevia",
    description: "Have questions about AI-powered ad compliance? Contact Rulevia's team for support, partnerships, or enterprise solutions. We're here to help optimize your advertising campaigns.",
    url: "/contact"
  },

  "/terms": {
    title: "Terms of Service - Rulevia",
    description: "Read Rulevia's Terms of Service. Learn about our AI-powered ad compliance platform usage terms, user responsibilities, and service agreements.",
    url: "/terms",
    noIndex: false
  },

  "/privacy": {
    title: "Privacy Policy - How Rulevia Protects Your Data",
    description: "Rulevia's Privacy Policy explains how we collect, use, and protect your data. Learn about our commitment to GDPR compliance and data security.",
    url: "/privacy",
    noIndex: false
  },

  "/cookies": {
    title: "Cookie Policy - Rulevia",
    description: "Understand how Rulevia uses cookies to improve your experience. Learn about cookie types, purposes, and how to manage your preferences.",
    url: "/cookies",
    noIndex: false
  },

  // App pages (authenticated)
  "/dashboard": {
    title: "Dashboard - Rulevia Creative Compliance",
    description: "Access your Rulevia dashboard to analyze ad creatives, track compliance scores, and manage campaigns across Meta, Google, TikTok, and YouTube.",
    url: "/dashboard",
    noIndex: true
  },

  "/projects": {
    title: "Projects - Manage Your Ad Campaigns | Rulevia",
    description: "Manage your advertising projects and creative campaigns. Organize, version, and collaborate on ad compliance analysis with your team.",
    url: "/projects",
    noIndex: true
  },

  "/analysis": {
    title: "Creative Analysis - AI Ad Compliance Check | Rulevia",
    description: "Analyze your ad creatives for platform compliance. Get instant AI-powered feedback for Meta, Google Ads, TikTok, and YouTube policies.",
    url: "/analysis",
    noIndex: true
  },

  "/credits": {
    title: "Credits & Billing - Manage Your Subscription | Rulevia",
    description: "Manage your Rulevia credits and subscription. View usage, upgrade plans, and track your ad analysis quota.",
    url: "/credits",
    noIndex: true
  },

  "/reports": {
    title: "Reports - Ad Compliance Analytics | Rulevia",
    description: "Generate detailed compliance reports for your advertising campaigns. Export insights and share with your team.",
    url: "/reports",
    noIndex: true
  },

  "/settings": {
    title: "Settings - Account Preferences | Rulevia",
    description: "Manage your Rulevia account settings, notifications, and preferences. Update your profile and team configurations.",
    url: "/settings",
    noIndex: true
  },

  "/team": {
    title: "Team Management - Collaborate on Ad Compliance | Rulevia",
    description: "Manage your team members, roles, and permissions. Collaborate effectively on ad compliance projects.",
    url: "/team",
    noIndex: true
  },

  "/recent-analysis": {
    title: "Recent Analysis - Your Latest Compliance Checks | Rulevia",
    description: "View your recent ad compliance analyses. Quick access to your latest creative reviews and compliance scores.",
    url: "/recent-analysis",
    noIndex: true
  },

  "/search": {
    title: "Search - Find Your Projects and Analysis | Rulevia",
    description: "Search through your projects, analyses, and creative content. Find what you need quickly with powerful search filters.",
    url: "/search",
    noIndex: true
  },

  "/ad-generator": {
    title: "AI Ad Generator - Create Compliant Ads | Rulevia",
    description: "Generate platform-compliant ads with AI. Create high-converting creatives that meet advertising guidelines automatically.",
    url: "/ad-generator",
    noIndex: true
  },

  // Authentication pages
  "/onboarding": {
    title: "Welcome to Rulevia - Get Started",
    description: "Complete your Rulevia onboarding to start analyzing ad compliance. Set up your account and begin optimizing your advertising campaigns.",
    url: "/onboarding",
    noIndex: true
  },

  "/forgot-password": {
    title: "Reset Password - Rulevia",
    description: "Reset your Rulevia account password. Enter your email to receive password reset instructions.",
    url: "/forgot-password",
    noIndex: true
  },

  "/reset-password": {
    title: "Set New Password - Rulevia",
    description: "Create a new password for your Rulevia account. Secure your access to AI-powered ad compliance tools.",
    url: "/reset-password",
    noIndex: true
  },

  // Payment pages
  "/payment/success": {
    title: "Payment Successful - Welcome to Rulevia",
    description: "Your subscription is now active! Start analyzing ad compliance and optimizing your advertising campaigns with AI.",
    url: "/payment/success",
    noIndex: true
  },

  "/payment/cancel": {
    title: "Payment Cancelled - Rulevia",
    description: "Your payment was cancelled. You can try again or contact support if you need assistance.",
    url: "/payment/cancel",
    noIndex: true
  },

  "/payment/failure": {
    title: "Payment Failed - Rulevia",
    description: "Payment processing failed. Please try again or contact support for assistance.",
    url: "/payment/failure",
    noIndex: true
  },

  // Other pages
  "/unsubscribe": {
    title: "Unsubscribe - Rulevia Email Preferences",
    description: "Manage your Rulevia email preferences. Unsubscribe from marketing emails or update your notification settings.",
    url: "/unsubscribe",
    noIndex: true
  }
};

// Product structured data for features/pricing
export const productStructuredData = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Rulevia AI Ad Compliance Platform",
  "description": "AI-powered creative compliance platform for advertising campaigns",
  "brand": {
    "@type": "Brand",
    "name": "Rulevia"
  },
  "category": "Marketing Software",
  "offers": [
    {
      "@type": "Offer",
      "name": "Free Trial",
      "price": "0",
      "priceCurrency": "USD",
      "description": "3 analyses per month"
    },
    {
      "@type": "Offer",
      "name": "Starter",
      "price": "15.90",
      "priceCurrency": "USD",
      "description": "25 analyses per month"
    },
    {
      "@type": "Offer",
      "name": "Pro",
      "price": "39.90",
      "priceCurrency": "USD",
      "description": "100 analyses per month"
    },
    {
      "@type": "Offer",
      "name": "Agency",
      "price": "99.90",
      "priceCurrency": "USD",
      "description": "350 analyses per month"
    },
    {
      "@type": "Offer",
      "name": "Enterprise",
      "price": "299.90",
      "priceCurrency": "USD",
      "description": "1000 analyses per month"
    }
  ]
};

// FAQ structured data
export const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What platforms does Rulevia support?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rulevia supports compliance analysis for Meta (Facebook/Instagram), Google Ads, TikTok, YouTube, Pinterest, and Microsoft Ads."
      }
    },
    {
      "@type": "Question",
      "name": "How does AI-powered compliance analysis work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rulevia uses advanced AI models to analyze your creative content against platform-specific advertising guidelines, providing instant feedback and compliance scores."
      }
    },
    {
      "@type": "Question",
      "name": "Can I try Rulevia for free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Rulevia offers a free trial with 3 analyses per month. No credit card required to get started."
      }
    }
  ]
};

// Helper function to get SEO config for a page
export function getPageSEO(pathname: string): SEOHeadProps {
  const pageConfig = pageSEOConfig[pathname] || {};
  return {
    ...defaultSEO,
    ...pageConfig
  };
}